import React from 'react';
import { useBooking } from '../context/BookingContext';
import { calculateRoomPrice } from '../utils/pricingCalculator';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const SHOP_ID = process.env.REACT_APP_SHOP_ID;
const RateOption = ({ rate, room, currentQuantity }) => {
  const { state, dispatch, formatCurrency, calculateNights } = useBooking();

  // --- Practical & Activity Based Inclusions ---
  const getInclusions = (rateObj) => {
    // Check if rate object has inclusions
    if (rateObj && rateObj.inclusions !== undefined) {
      // If inclusions is an array with items
      if (Array.isArray(rateObj.inclusions) && rateObj.inclusions.length > 0) {
        // Extract titles from inclusion objects
        return rateObj.inclusions
          .map(inclusion => {
            if (typeof inclusion === 'string') return inclusion;
            if (typeof inclusion === 'object') return inclusion.title || inclusion.name;
            return String(inclusion);
          })
          .filter(title => title && title.trim() !== '');
      }
      // If inclusions is an empty array, return empty array (don't show any inclusions)
      if (Array.isArray(rateObj.inclusions) && rateObj.inclusions.length === 0) {
        return [];
      }
    }

    // Only show fallback if there are NO inclusions property at all or it's null/undefined
    // Don't show fallback for empty arrays
    
    const name = rateObj.name ? rateObj.name.toLowerCase() : '';
    
    if (name.includes('ap')) {
      return [
        'All Meals Included (Breakfast, Lunch, Dinner)',
        'Gala Dinner included (Worth ₹2500)',
        'Comp. Airport Transfer (Two-way)',
        'Jeep Safari (Evening Slot)'
      ];
    } else if (name.includes('map') || name.includes('mep')) {
      return [
        'Breakfast & Dinner Buffet',
        'Bonfire & Musical Evening',
        'Guided Nature Walk / Jungle Trail',
        '15% Discount on Spa Services'
      ];
    } else if (name.includes('cp')) {
      return [
        'Buffet Breakfast',
        'Welcome Drink on Arrival (Non-Alcoholic)',
        'High Speed Wi-Fi',
        '2 Bottles Mineral Water Daily'
      ];
    } else if (name.includes('ep')) {
      return [
        'Room Only',
        'Access to Swimming Pool',
        'Gym / Fitness Centre Usage',
        'Free Parking'
      ];
    }
    
    return ['Complimentary Wi-Fi', 'Access to Hotel Amenities'];
  };

  const inclusions = getInclusions(rate);

  // --- NEW: Get next available room number ---
  const getNextAvailableRoomNumber = () => {
    const usedRoomNumbers = state.cart.map(item => item.roomNumber);
    let nextRoomNumber = 1;
    
    // Find the first unused room number
    while (usedRoomNumbers.includes(nextRoomNumber)) {
      nextRoomNumber++;
    }
    
    return nextRoomNumber;
  };

  // --- NEW: Get guest configuration for a specific room number ---
  const getGuestConfigForRoom = (roomNumber) => {
    const guestConfig = state.searchState.room_guest.find(
      guest => guest.room_no === roomNumber
    );
    
    if (guestConfig) {
      return guestConfig;
    }
    
    // Fallback to first guest configuration
    return state.searchState.room_guest[0];
  };

  // --- NEW: Get cart items for this specific rate/room combo ---
  const getCartItemsForThisRate = () => {
    return state.cart.filter(item => 
      item.id === rate.id && item.roomId === room.id
    );
  };

  // --- NEW: Get cart items for this ROOM CATEGORY (all rates in this category) ---
  const getCartItemsForThisRoomCategory = () => {
    return state.cart.filter(item => 
      item.roomId === room.id  // All rooms in this category, regardless of rate
    );
  };

  // --- NEW: Get current room count for THIS RATE ---
  const getCurrentRoomCountForThisRate = () => {
    return getCartItemsForThisRate().length;
  };

  // --- NEW: Get current room count for THIS ROOM CATEGORY (all rates) ---
  const getCurrentRoomCountForThisCategory = () => {
    return getCartItemsForThisRoomCategory().length;
  };

  const currentRoomCountForThisRate = getCurrentRoomCountForThisRate();
  const currentRoomCountForThisCategory = getCurrentRoomCountForThisCategory();

  // Calculate room limits
  const totalRoomsAvailable = room.total_rooms || 0;
  const totalRoomsSelectedGlobally = state.cart.length;
  const maxRoomsAllowed = state.searchState.room_guest.length;
  
  // FIXED: Use currentRoomCountForThisCategory for category limit check
  const isCategoryLimitReached = currentRoomCountForThisCategory >= totalRoomsAvailable;
  const isGlobalLimitReached = totalRoomsSelectedGlobally >= maxRoomsAllowed;
  const canAddMoreInThisCategory = currentRoomCountForThisCategory < totalRoomsAvailable;

  // --- NEW: Calculate pricing for specific guest configuration ---
  const getPricingForGuests = (adults, children) => {
    const adultsCount = Math.min(adults, room.maxGuests || 3);
    const childrenCount = Math.min(children, 2);
    
    // Calculate price with guest-based pricing
    let totalOriginalPrice = 0;
    let totalDiscountedPrice = 0;

    // Calculate adult pricing
    if (rate.adult_pricing && Array.isArray(rate.adult_pricing)) {
      const adultPricing = rate.adult_pricing.find(pricing => pricing.adults_count === adultsCount) || 
                          rate.adult_pricing[rate.adult_pricing.length - 1];
      
      if (adultPricing) {
        totalOriginalPrice += adultPricing.original_price || adultPricing.price;
        totalDiscountedPrice += adultPricing.price;
      }
    }

    // Calculate child pricing
    if (rate.child_pricing && Array.isArray(rate.child_pricing) && childrenCount > 0) {
      const childPricing = rate.child_pricing.find(pricing => pricing.children_count === childrenCount) || 
                          rate.child_pricing[rate.child_pricing.length - 1];
      
      if (childPricing) {
        totalOriginalPrice += childPricing.original_price || 0;
        totalDiscountedPrice += childPricing.price || 0;
      }
    }

    // Use discounted price if available, otherwise calculate from rate
    if (totalDiscountedPrice > 0) {
      return totalDiscountedPrice;
    }
    
    // Fallback to calculateRoomPrice
    return calculateRoomPrice(rate, adultsCount, childrenCount);
  };

  // --- NEW: Calculate guest pricing for display (uses first available room config) ---
  const calculateGuestBasedPricing = () => {
    // Get pricing for the next available room
    const nextRoomNumber = getNextAvailableRoomNumber();
    const guestConfig = getGuestConfigForRoom(nextRoomNumber);
    
    const adultsCount = Math.min(guestConfig.no_of_adults, room.maxGuests || 3);
    const childrenCount = Math.min(guestConfig.no_of_children, 2);
    
    let totalOriginalPrice = 0;
    let totalDiscountedPrice = 0;

    // Calculate adult pricing
    if (rate.adult_pricing && Array.isArray(rate.adult_pricing)) {
      const adultPricing = rate.adult_pricing.find(pricing => pricing.adults_count === adultsCount) || 
                          rate.adult_pricing[rate.adult_pricing.length - 1];
      
      if (adultPricing) {
        totalOriginalPrice += adultPricing.original_price || adultPricing.price;
        totalDiscountedPrice += adultPricing.price;
      }
    }

    // Calculate child pricing
    if (rate.child_pricing && Array.isArray(rate.child_pricing) && childrenCount > 0) {
      const childPricing = rate.child_pricing.find(pricing => pricing.children_count === childrenCount) || 
                          rate.child_pricing[rate.child_pricing.length - 1];
      
      if (childPricing) {
        totalOriginalPrice += childPricing.original_price || 0;
        totalDiscountedPrice += childPricing.price || 0;
      }
    }

    return {
      originalPrice: totalOriginalPrice,
      discountedPrice: totalDiscountedPrice > 0 ? totalDiscountedPrice : calculateRoomPrice(rate, adultsCount, childrenCount),
      hasDiscount: totalOriginalPrice > totalDiscountedPrice
    };
  };

  const guestPricing = calculateGuestBasedPricing();
  const calculatedPrice = guestPricing.discountedPrice;

  // Get discount information from offer object
  const getDiscountInfo = () => {
    if (rate.offer && rate.offer.discount_name) {
      const { discount_type, discount_value, discount_percentage, discount_amount } = rate.offer;
      
      if (discount_type === 'percentage') {
        return {
          name: rate.offer.discount_name,
          type: 'percentage',
          value: discount_percentage,
          rawValue: discount_value,
          amount: discount_amount
        };
      } else if (discount_type === 'fixed') {
        return {
          name: rate.offer.discount_name,
          type: 'fixed',
          value: formatCurrency(discount_value),
          rawValue: discount_value,
          amount: discount_amount
        };
      }
    }
    
    // Calculate discount from guest pricing
    if (guestPricing.hasDiscount && guestPricing.originalPrice > 0) {
      const discount = ((guestPricing.originalPrice - guestPricing.discountedPrice) / guestPricing.originalPrice) * 100;
      if (discount > 0) {
        return {
          name: rate.offer.discount_name,
          type: 'percentage',
          value: `${Math.round(discount)}%`,
          rawValue: Math.round(discount)
        };
      }
    }
    
    return null;
  };

  const discountInfo = getDiscountInfo();

  // Format discount display text
  const getDiscountDisplayText = () => {
    if (!discountInfo) return null;
    
    if (discountInfo.type === 'percentage') {
      return `${discountInfo.value} OFF`;
    } else if (discountInfo.type === 'fixed') {
      return `${discountInfo.value} OFF`;
    }
    return null;
  };

  // Calculate savings amount
  const calculateSavings = () => {
    if (discountInfo) {
      if (discountInfo.type === 'percentage' && guestPricing.originalPrice > 0) {
        const discountDecimal = discountInfo.rawValue / 100;
        return guestPricing.originalPrice * discountDecimal;
      } else if (discountInfo.type === 'fixed') {
        return discountInfo.rawValue;
      }
    }
    
    // Calculate from guest pricing difference
    if (guestPricing.hasDiscount && guestPricing.originalPrice > 0) {
      return guestPricing.originalPrice - guestPricing.discountedPrice;
    }
    
    return 0;
  };

  const savingsAmount = calculateSavings();

  // --- UPDATED: handleRateAction with proper category counting ---
  const handleRateAction = async (action) => {
    const { checkin, checkout } = state.searchState;
    if (!checkin || !checkout || checkout <= checkin) {
      alert("Please select valid Check-in and Check-out dates first.");
      return;
    }

    const sessionData = { ...state };

    if (action === 'select' || action === 'plus') {
      // Check global room limit
      if (isGlobalLimitReached) {
        alert(`You have already selected the maximum of ${maxRoomsAllowed} room${maxRoomsAllowed > 1 ? 's' : ''} needed for your search.`);
        return;
      }

      // Check category availability - FIXED: use currentRoomCountForThisCategory
      if (isCategoryLimitReached) {
        alert(`Only ${totalRoomsAvailable} room${totalRoomsAvailable !== 1 ? 's' : ''} available in ${room.name}.`);
        return;
      }

      // For BOTH 'select' and 'plus', assign a NEW room
      const nextRoomNumber = getNextAvailableRoomNumber();
      const guestConfig = getGuestConfigForRoom(nextRoomNumber);
      
      if (!guestConfig) {
        alert(`No guest configuration found for room ${nextRoomNumber}`);
        return;
      }

      // Calculate price for this specific guest configuration
      const roomPrice = getPricingForGuests(guestConfig.no_of_adults, guestConfig.no_of_children);
      
      // Calculate base price for savings calculation
      const basePrice = rate.base_price || rate.originalPrice || roomPrice;
      
      // Create a new cart item for the new room
      const newCartItem = {
        id: rate.id,
        roomId: room.id,
        roomNumber: nextRoomNumber,
        roomName: room.name,
        rateName: rate.name,
        roomPrice: roomPrice,
        basePrice: basePrice,
        adultsCount: Math.min(guestConfig.no_of_adults, room.maxGuests || 3),
        childrenCount: Math.min(guestConfig.no_of_children, 2),
        imageUrl: room.images[0],
        discountInfo: discountInfo ? {
          name: discountInfo.name,
          type: discountInfo.type,
          value: discountInfo.value,
          rawValue: discountInfo.rawValue
        } : null,
        offerName: discountInfo ? discountInfo.name : null,
        offerFreeNights: rate.offerFreeNights || 0,
        quantity: 1, // Each cart item represents ONE room
        guestInfo: {
          adults: guestConfig.no_of_adults,
          children: guestConfig.no_of_children
        },
        originalGuestConfig: {
          adults: guestConfig.no_of_adults,
          children: guestConfig.no_of_children,
          roomNo: nextRoomNumber
        },
        savings: savingsAmount
      };
      
      sessionData.cart.push(newCartItem);
      
    } else if (action === 'minus') {
      // Find all items for this rate/room combo
      const rateItems = getCartItemsForThisRate();
      
      if (rateItems.length > 0) {
        // Remove the item with the highest room number (most recent)
        const lastAddedItem = rateItems.reduce((prev, current) => 
          (prev.roomNumber > current.roomNumber) ? prev : current
        );
        
        // Remove this item from cart
        const indexToRemove = sessionData.cart.findIndex(item => 
          item.id === lastAddedItem.id && 
          item.roomId === lastAddedItem.roomId && 
          item.roomNumber === lastAddedItem.roomNumber
        );
        
        if (indexToRemove > -1) {
          sessionData.cart.splice(indexToRemove, 1);
        }
      }
    }

    const nights = calculateNights();
    dispatch({ type: 'UPDATE_CART', payload: sessionData.cart });
    
    // Calculate taxes dynamically after cart update
    const calculateTaxesForCart = async () => {
      try {
        // Format dates for API
        const formatDate = (date) => {
          if (date instanceof Date) {
            return date.toISOString().split('T')[0];
          }
          return new Date(date).toISOString().split('T')[0];
        };

        const nights = calculateNights();
        
        // Array to store all tax API calls
        const taxPromises = [];
        
        // Create API call for each room in the cart
        sessionData.cart.forEach((item) => {
          // Pass PER NIGHT price to API
          const perNightPrice = item.roomPrice;
          
          const payload = {
            type: "tariff",
            amount: perNightPrice,
            check_in_date: formatDate(checkin),
            check_out_date: formatDate(checkout),
            id_shop: SHOP_ID
          };

          // Add API call promise to array
          taxPromises.push(
            fetch(`${API_BASE_URL}/fo/mstCalculation`, {
              method: 'POST',
              headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(payload)
            })
            .then(response => response.json())
            .then(data => {
              if (data.total_tax) {
                const totalTax = data.total_tax;
                return {
                  roomName: item.roomName,
                  roomPrice: perNightPrice,
                  perNightPrice: perNightPrice,
                  taxData: {
                    base_amount_total: totalTax.base_amount_total || 0,
                    total_cgst_amount_after_tax: totalTax.total_cgst_amount_after_tax || 0,
                    total_sgst_amount_after_tax: totalTax.total_sgst_amount_after_tax || 0,
                    total_cess_amount: totalTax.total_cess_amount || 0,
                    grand_total: totalTax.grand_total || 0
                  }
                };
              }
              return null;
            })
          );
        });

        // Wait for all API calls to complete
        const responses = await Promise.all(taxPromises);
        
        // Filter out null responses
        const validResponses = responses.filter(r => r !== null);
        
        // Calculate cumulative totals
        const cumulativeTotals = {
          base_amount_total: 0,
          total_cgst_amount_after_tax: 0,
          total_sgst_amount_after_tax: 0,
          total_cess_amount: 0,
          grand_total: 0,
          perNightBaseTotal: 0
        };
        
        const roomBreakdowns = [];
        
        validResponses.forEach((roomData, index) => {
          // Add to cumulative totals
          cumulativeTotals.base_amount_total += roomData.taxData.base_amount_total;
          cumulativeTotals.total_cgst_amount_after_tax += roomData.taxData.total_cgst_amount_after_tax;
          cumulativeTotals.total_sgst_amount_after_tax += roomData.taxData.total_sgst_amount_after_tax;
          cumulativeTotals.total_cess_amount += roomData.taxData.total_cess_amount;
          cumulativeTotals.grand_total += roomData.taxData.grand_total;
          cumulativeTotals.perNightBaseTotal += roomData.perNightPrice;
          
          // Store per-room breakdown for display
          roomBreakdowns.push({
            roomIndex: index + 1,
            roomName: `${roomData.roomName} ${index + 1}`,
            perNightPrice: roomData.perNightPrice,
            totalForStay: roomData.taxData.base_amount_total,
            taxBreakdown: {
              base_amount_total: roomData.taxData.base_amount_total,
              total_cgst_amount_after_tax: roomData.taxData.total_cgst_amount_after_tax,
              total_sgst_amount_after_tax: roomData.taxData.total_sgst_amount_after_tax,
              total_cess_amount: roomData.taxData.total_cess_amount,
              grand_total: roomData.taxData.grand_total
            }
          });
        });
        
        // Calculate room rate total from cart items (per night × nights)
        const rateTotal = sessionData.cart.reduce((sum, item) => 
          sum + (item.roomPrice * nights), 0
        );
        
        const totalTaxes = cumulativeTotals.total_cgst_amount_after_tax + 
                          cumulativeTotals.total_sgst_amount_after_tax + 
                          cumulativeTotals.total_cess_amount;
        
        // Update state with the new totals
        dispatch({
          type: 'UPDATE_TOTALS',
          payload: {
            rateTotal: rateTotal,
            subtotal: cumulativeTotals.base_amount_total,
            taxes: totalTaxes,
            total: cumulativeTotals.grand_total,
            finalTotal: cumulativeTotals.grand_total,
            taxBreakdown: {
              cgst: cumulativeTotals.total_cgst_amount_after_tax,
              sgst: cumulativeTotals.total_sgst_amount_after_tax,
              cess: cumulativeTotals.total_cess_amount,
              totalTaxPercentage: totalTaxes > 0 ? ((totalTaxes / cumulativeTotals.base_amount_total) * 100).toFixed(2) : '0.00',
              perRoomBreakdowns: roomBreakdowns,
              nights: nights,
              perNightBaseTotal: cumulativeTotals.perNightBaseTotal
            }
          }
        });

      } catch (error) {
        console.error('Tax calculation error:', error);
        
        // Fallback to static calculation if any API call fails
        const nights = calculateNights();
        const totalRoomRate = sessionData.cart.reduce((sum, item) => 
          sum + (item.roomPrice * nights), 0
        );
        const rateTotal = totalRoomRate;
        const subtotal = rateTotal - (state.offerDiscount || 0);
        const taxes = subtotal * 0.18;
        const total = subtotal + taxes;
        
        dispatch({
          type: 'UPDATE_TOTALS',
          payload: {
            rateTotal,
            subtotal,
            taxes,
            total,
            finalTotal: total,
            taxBreakdown: null
          }
        });
      }
    };
    
    // Call the tax calculation function
    await calculateTaxesForCart();
  };

  // --- NEW: Get display room number for selected rooms ---
  const getDisplayRoomNumber = () => {
    const rateItems = getCartItemsForThisRate();
    if (rateItems.length > 0) {
      const roomNumbers = rateItems.map(item => item.roomNumber).sort((a, b) => a - b);
      if (roomNumbers.length === 1) {
        return `Room ${roomNumbers[0]}`;
      } else {
        return `Rooms ${roomNumbers.join(', ')}`;
      }
    }
    return null;
  };

  const displayRoomNumber = getDisplayRoomNumber();

  // Show guest breakdown for the NEXT available room
  const nextRoomGuest = getGuestConfigForRoom(getNextAvailableRoomNumber());
  const showGuestBreakdown = nextRoomGuest.no_of_adults > 1 || nextRoomGuest.no_of_children > 0;

  return (
    <div className="rate-option-row group flex flex-col sm:flex-row sm:items-center justify-start gap-6 py-1.5 transition-colors duration-150 hover:bg-white sm:hover:shadow-sm sm:hover:-mx-4 sm:hover:px-4 sm:hover:rounded-md border-t border-gray-200 first:border-t-0">
      <div className="flex-grow mb-2 sm:mb-0 sm:min-w-[150px] sm:max-w-xs">
        <p className="text-sm font-semibold text-text-secondary transition-colors duration-150">
          {rate.name}
        </p>

        {inclusions && inclusions.length > 0 && (
          <div className="mt-2 flex flex-col gap-1">
            {inclusions.map((inc, index) => (
              <div key={index} className="flex items-start">
                <svg 
                  className="w-3.5 h-3.5 mr-2 text-green-600 flex-shrink-0 mt-0.5" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
                <span className="text-xs text-text-secondary leading-snug">
                  {inc}
                </span>
              </div>
            ))}
          </div>
        )}
        
        {displayRoomNumber && (
          <p className="text-xs text-gray-500 mt-1">
            {displayRoomNumber}
          </p>
        )}
      </div>
      
      <div className="flex-shrink-0 flex items-center justify-end flex-wrap gap-x-4 gap-y-1 ml-auto">
        <div className="flex flex-col items-end">
          {/* Total Price Display */}
          <div className="flex items-center gap-2">
            {/* Show original price with strikethrough if there's a discount */}
            {((guestPricing.hasDiscount && guestPricing.originalPrice > 0) || discountInfo) && (
              <span className="text-xs sm:text-sm text-gray-500 line-through">
                {formatCurrency(guestPricing.originalPrice)}
              </span>
            )}
            <p className="text-lg font-bold text-text-primary -mt-0.5">
              {formatCurrency(calculatedPrice)}
            </p>
          </div>
          
          <span className="text-[10px] text-gray-500">Per night</span>
        </div>
        
        <div className="rate-counter-controls w-28 text-center">
          {currentRoomCountForThisRate === 0 ? (
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => handleRateAction('select')}
                disabled={isGlobalLimitReached || isCategoryLimitReached}
                className={`w-full text-center bg-brand-accent text-text-dark-on-accent border border-brand-accent px-3 py-1.5 rounded-md font-semibold text-sm hover:bg-brand-accent-hover transition-all duration-200 shadow-sm transform hover:scale-105 ${
                  (isGlobalLimitReached || isCategoryLimitReached) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Select
              </button>
            </div>
          ) : (
            <div className="rate-counter flex flex-col items-center">
              <div className="flex items-center justify-between gap-1">
                <button
                  onClick={() => handleRateAction('minus')}
                  className="w-8 h-8 rounded-full border border-gray-300 text-text-primary text-lg font-bold flex items-center justify-center hover:bg-slate-100"
                >
                  -
                </button>
                <span className="rate-quantity font-bold text-lg text-text-primary w-8 text-center">
                  {currentRoomCountForThisRate}
                </span>
                <button
                  onClick={() => handleRateAction('plus')}
                  disabled={isGlobalLimitReached || !canAddMoreInThisCategory}
                  className={`w-8 h-8 rounded-full border border-gray-300 text-text-primary text-lg font-bold flex items-center justify-center hover:bg-slate-100 ${
                    (isGlobalLimitReached || !canAddMoreInThisCategory) ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  +
                </button>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {currentRoomCountForThisCategory}/{totalRoomsAvailable} in category
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateOption;